<script lang="ts">
	/**
	 * Site header, matching the live dutchpassion.co.th layout:
	 *
	 *   1. top bar   — Customer Service, FAQ, About Us | Sign In
	 *   2. masthead  — logo left, then search and the three trust points
	 *   3. wide nav  — Cannabis Seeds (dropdown), Seed Sale, News & Blog
	 *
	 * The trust points sit *inside* the masthead as a three-column row, not as a
	 * separate full-width band beneath it — an earlier version got that wrong and
	 * the header read as one tier too many.
	 *
	 * The live masthead also carries a cart widget. It is omitted until checkout
	 * exists: a cart icon that cannot hold anything is worse than none.
	 */
	import { m } from '$lib/paraglide/messages';
	import Logo from '../Logo.svelte';
	import SearchBar from '../SearchBar.svelte';
	import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.svelte';
	import UtilityBar from './UtilityBar.svelte';
	import CategoryNav from './CategoryNav.svelte';
	import MobileMenu from './MobileMenu.svelte';
	import CartLink from '../cart/CartLink.svelte';

	let isOpen = $state(false);

	const trustPoints = $derived([
		{ title: m.discreet_shipping(), sub: m.discreet_shipping_sub() },
		{ title: m.years_experience(), sub: m.years_experience_sub() },
		{ title: m.safe_payment(), sub: m.safe_payment_sub() }
	]);
</script>

<!--
	On `lg` the logo is a tall block on the left and the three rows — utility,
	search + trust + cart, navigation — stack in a column beside it, as on
	dutch-passion.com. Below `lg` it collapses to logo, cart, language and the
	burger, unchanged.

	The trust points stay inside this block rather than becoming a band of their
	own. That was tried before and rejected as one tier too many, and the
	reference does the same thing: they share a row with the search field.
-->
<!--
	`relative z-30` is what keeps anything that opens *out* of this header — the
	mobile menu, the category dropdown, the language dialog — above the rest of
	the page.

	Without it the header is unpositioned and therefore paints below every
	positioned element, and `ProductPager` on a strain page is `sticky top-0
	z-20`: the prev/next cards sat on top of the opened menu. The category
	dropdown lost the same fight for a subtler reason — it is also `z-20`, but
	the pager comes later in the document, so a tie resolves in the pager's
	favour.

	One stacking context on the header settles both at once, and the z-indexes
	inside it (up to the dialog's `z-50`) are now scoped to it rather than
	competing with page content.
-->
<header class="relative z-30 border-b border-strong bg-surface">
	<!--
		`lg:pt-0 lg:pb-0`: on desktop the utility bar is the first row of the stack
		and the category nav is the last, and both bring their own padding — the
		container's `py-3` was stacking on top of that, so the header carried a
		band of dead space above the utility bar and left the nav's `after:bottom-0`
		underline floating 12px above the header border instead of meeting it.
		Mobile keeps the padding: there the row is just the logo and the burger.
	-->
	<div class="mx-auto flex max-w-7xl items-center gap-3 px-6 py-3 lg:items-stretch lg:gap-10 lg:py-0">
		<!--
			Full height of the stack on `lg`, so the three rows read as one unit.
			A plain wrapper, not a link: `Logo` renders its own anchor, and nesting
			one inside another is invalid and collapses unpredictably.
		-->
		<div class="flex shrink-0 items-center">
			<Logo />
		</div>

		<div class="hidden min-w-0 flex-1 flex-col lg:flex">
			<UtilityBar />

			<div class="flex items-center gap-8 py-3">
				<div class="w-full max-w-xs shrink-0 xl:max-w-sm"><SearchBar /></div>

				<!--
					Trust points, sharing the search row as on the reference — but only
					from `xl`. The logo is ~224px wide at this size and the search field
					takes 320 more; at `lg` that leaves the three points about 100px
					each, which wraps every title onto two lines. Between `lg` and `xl`
					the row is search and cart alone.
				-->
				<ul class="hidden min-w-0 flex-1 items-start justify-between gap-6 xl:flex">
					{#each trustPoints as point (point.title)}
						<li class="flex items-start gap-2">
							<svg
								class="mt-0.5 h-4 w-4 shrink-0 text-secondary-600"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fill-rule="evenodd"
									d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
									clip-rule="evenodd"
								/>
							</svg>
							<span class="leading-tight">
								<span class="block text-xs font-bold tracking-wide text-body uppercase">
									{point.title}
								</span>
								<span class="block text-xs text-muted">{point.sub}</span>
							</span>
						</li>
					{/each}
				</ul>

				<div class="shrink-0"><CartLink /></div>
			</div>

			<CategoryNav />
		</div>

		<div class="ml-auto flex items-center gap-1 sm:gap-2 lg:hidden">
			<CartLink />
			<LanguageSwitcher />
			<button
				type="button"
				class="inline-flex items-center rounded-md p-2 text-body transition-colors hover:bg-surface-muted"
				aria-expanded={isOpen}
				aria-controls="mobile-menu"
				onclick={() => (isOpen = !isOpen)}
			>
				<span class="sr-only">{isOpen ? m.close_menu() : m.menu()}</span>
				<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
					{#if isOpen}
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18 18 6M6 6l12 12" />
					{:else}
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
					{/if}
				</svg>
			</button>
		</div>
	</div>

	<div id="mobile-menu">
		<MobileMenu open={isOpen} onclose={() => (isOpen = false)} />
	</div>
</header>
